/**
 * Mock KG drawer API — seeds responses from sample graph data by node id.
 */

import {
  sampleEdges,
  sampleNodes,
} from "../../components/kg/canvas/data/sampleData";
import type {
  KgFlowEdge,
  KgFlowNode,
  KgFlowNodeType,
  KgNodeDetails,
} from "../../components/kg/canvas/types";
import { emptyKgPageInfo } from "./kgApi.types";
import type {
  KgDataSourceReportsResponse,
  KgDataSourceTablesResponse,
  KgDataSourceUsedInResponse,
  KgDrawerListParams,
  KgNodeOverviewResponse,
  KgOntologyLinksResponse,
  KgOntologyMetricsResponse,
  KgDataModelMetricsResponse,
  KgDataModelOverviewResponse,
  KgDataModelRelationshipsResponse,
  KgDataModelTablesResponse,
  KgOntologyOverviewResponse,
  KgMetricFormulaResponse,
  KgMetricOverviewResponse,
  KgOntologyReportsResponse,
  KgOntologyRulesResponse,
  KgOverviewField,
  KgDataStoryActivityResponse,
  KgDataStoryOverviewResponse,
  KgDataStoryTraceResponse,
  KgArtifactActivityResponse,
  KgArtifactOverviewResponse,
  KgReportActivityResponse,
  KgReportOverviewResponse,
  KgReportSourceType,
  KgReportTraceResponse,
  KgTraceSectionKey,
  KgRelationType,
  KgSchemaMetricsResponse,
  KgSchemaRelationshipsParams,
  KgSchemaRelationshipsResponse,
  KgTableOverviewResponse,
  KgTablePreviewRow,
  KgUsedInSectionKey,
} from "./kgDrawerApi.types";

const MOCK_DELAY_MS = 120;

/** Placeholder link for mock used-in / metrics rows (Figma shows icon on every row). */
const MOCK_EXTERNAL_LINK = "#";

const usedInApiItem = (
  id: string,
  name: string,
  externalUrl = MOCK_EXTERNAL_LINK,
) => ({
  id,
  name,
  externalUrl,
});

const delay = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, MOCK_DELAY_MS);
  });

const findNode = (nodeId: string): KgFlowNode | undefined =>
  sampleNodes.find((node) => node.id === nodeId);

const toTableSlug = (title: string) =>
  title.trim().toLowerCase().replace(/\s+/g, "_");

const formatDisplayDate = (iso?: string) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const collectDescendantIds = (
  rootId: string,
  edges: KgFlowEdge[],
): Set<string> => {
  const seen = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const edge of edges) {
      if (edge.source === id && !seen.has(edge.target)) {
        seen.add(edge.target);
        queue.push(edge.target);
      }
    }
  }
  seen.delete(rootId);
  return seen;
};

/** Figma/demo labels → sample-graph canvas ids when not in the descendant subtree. */
const DEMO_LABEL_CANVAS_IDS: Partial<
  Record<KgFlowNodeType, Record<string, string>>
> = {
  ontology: {
    Order: "ont-order",
    Region: "ont-region",
    Product: "ont-product",
    Batch: "ont-batch",
    Customer: "ont-customer",
    Campaign: "ont-campaign",
  },
  dataStory: {
    "Q3 revenue review": "dstory-q3",
  },
};

/** Map a used-in row label to a descendant canvas node for inspector focus. */
const resolveUsedInItemFromGraph = (
  rootId: string,
  nodeType: KgFlowNodeType,
  label: string,
  fallbackId: string,
): { id: string; name: string; href?: string } => {
  const descendantIds = collectDescendantIds(rootId, sampleEdges);
  const match = sampleNodes.find(
    (node) =>
      descendantIds.has(node.id) &&
      node.type === nodeType &&
      (node.data.title ?? node.id).trim() === label.trim(),
  );

  const canvasId = match?.id ?? DEMO_LABEL_CANVAS_IDS[nodeType]?.[label.trim()];

  if (!canvasId) {
    return { id: fallbackId, name: label };
  }

  return {
    id: canvasId,
    name: label,
    href: `/graph?focus=${canvasId}`,
  };
};

const statusToLabel = (status?: KgNodeDetails["status"]) => {
  switch (status) {
    case "connected":
      return "Connected";
    case "disconnected":
      return "Disconnected";
    case "error":
      return "Error";
    case "pending":
      return "Pending";
    default:
      return "Unknown";
  }
};

const buildOverviewFields = (details?: KgNodeDetails): KgOverviewField[] => {
  const instanceNames =
    details?.instanceNames ??
    (details?.instances != null && details.instances > 0
      ? Array.from({ length: details.instances }, (_, i) => `Instance ${i + 1}`)
      : []);

  const fields: KgOverviewField[] = [];

  if (details?.type) {
    fields.push({
      key: "type",
      label: "Type",
      valueType: "text",
      value: details.type,
    });
  }

  if (details?.status) {
    fields.push({
      key: "status",
      label: "Status",
      valueType: "status",
      value: statusToLabel(details.status),
    });
  }

  if (details?.connectedOn) {
    fields.push({
      key: "connectedOn",
      label: "Connected on",
      valueType: "date",
      value: details.connectedOn,
    });
  }

  if (instanceNames.length > 0) {
    fields.push({
      key: "instances",
      label: "Instances",
      valueType: "tags",
      value: instanceNames,
    });
  }

  return fields;
};

const deriveTablesFromGraph = (
  dataSourceId: string,
): KgDataSourceTablesResponse => {
  const tableIds = new Set(
    sampleEdges
      .filter((edge) => edge.source === dataSourceId)
      .map((edge) => edge.target),
  );

  const rows = sampleNodes
    .filter((node) => node.type === "table" && tableIds.has(node.id))
    .map((node) => {
      const pk =
        node.data.fields?.find((field) => field.keyType === "PK")?.name ?? "—";
      return {
        id: node.id,
        name: toTableSlug(node.data.title ?? node.id),
        primaryKey: pk,
      };
    });

  return { rows, pageInfo: emptyKgPageInfo(rows.length) };
};

const deriveReportsFromGraph = (
  dataSourceId: string,
  details?: KgNodeDetails,
): KgDataSourceReportsResponse => {
  const descendantIds = collectDescendantIds(dataSourceId, sampleEdges);
  const connectedOn = formatDisplayDate(details?.connectedOn);

  const rows = sampleNodes
    .filter((node) => node.type === "report" && descendantIds.has(node.id))
    .map((node) => ({
      id: node.id,
      title: node.data.title ?? node.id,
      connectedOn,
    }));

  return { rows, pageInfo: emptyKgPageInfo(rows.length) };
};

const SECTION_META: Record<
  KgUsedInSectionKey,
  { label: string; nodeType: KgFlowNodeType }
> = {
  ontologies: { label: "Ontologies", nodeType: "ontology" },
  stories: { label: "Data stories", nodeType: "dataStory" },
  dataStories: { label: "Data stories", nodeType: "dataStory" },
  reports: { label: "Reports", nodeType: "report" },
  /** Matches `USED_IN_SECTION_NODE_TYPE` in kgDrawerMappers (legacy key → report nodes). */
  metrics: { label: "Metrics", nodeType: "report" },
};

const deriveUsedInFromGraph = (
  dataSourceId: string,
): KgDataSourceUsedInResponse => {
  const descendantIds = collectDescendantIds(dataSourceId, sampleEdges);
  const children = sampleNodes.filter((node) => descendantIds.has(node.id));

  const sections = (Object.keys(SECTION_META) as KgUsedInSectionKey[]).map(
    (key) => {
      const { label, nodeType } = SECTION_META[key];
      const items = children
        .filter((node) => node.type === nodeType)
        .map((node) => ({
          id: node.id,
          name: node.data.title ?? node.id,
          href: `/graph?focus=${node.id}`,
        }));
      return {
        key,
        label,
        items,
        pageInfo: emptyKgPageInfo(items.length),
      };
    },
  );

  return { sections };
};

const ORDERS_RELATIONSHIPS: KgSchemaRelationshipsResponse["rows"] = [
  {
    id: "rel-order-item",
    name: "order_item",
    nameHref: MOCK_EXTERNAL_LINK,
    column: "order_id",
    relationType: "one_to_many",
    relationLabel: "One to many",
  },
  {
    id: "rel-customers",
    name: "customers",
    nameHref: MOCK_EXTERNAL_LINK,
    column: "customer_id",
    relationType: "many_to_one",
    relationLabel: "Many to one",
  },
  {
    id: "rel-regions",
    name: "regions",
    nameHref: MOCK_EXTERNAL_LINK,
    column: "region_id",
    relationType: "many_to_one",
    relationLabel: "Many to one",
  },
  {
    id: "rel-shipments",
    name: "shipments",
    nameHref: MOCK_EXTERNAL_LINK,
    column: "order_id",
    relationType: "one_to_many",
    relationLabel: "One to many",
  },
  {
    id: "rel-returns",
    name: "returns",
    nameHref: MOCK_EXTERNAL_LINK,
    column: "order_id",
    relationType: "one_to_many",
    relationLabel: "One to many",
  },
];

const ORDERS_METRICS: KgSchemaMetricsResponse["items"] = [
  {
    id: "mt-gross-sales",
    name: "Gross sales ($)",
    href: MOCK_EXTERNAL_LINK,
    externalUrl: null,
  },
  {
    id: "mt-aov",
    name: "Avg order value",
    href: MOCK_EXTERNAL_LINK,
    externalUrl: null,
  },
  {
    id: "mt-order-count",
    name: "Order count",
    href: MOCK_EXTERNAL_LINK,
    externalUrl: null,
  },
  {
    id: "mt-regional-share",
    name: "Regional sales share (%)",
    href: MOCK_EXTERNAL_LINK,
    externalUrl: null,
  },
];

const relationTypeFromLabel = (label: string): KgRelationType => {
  const normalized = label.trim().toLowerCase().replace(/\s+/g, "_");
  if (
    normalized === "one_to_one" ||
    normalized === "one_to_many" ||
    normalized === "many_to_one" ||
    normalized === "many_to_many"
  ) {
    return normalized;
  }
  if (normalized.includes("many_to_one")) return "many_to_one";
  if (normalized.includes("one_to_many")) return "one_to_many";
  if (normalized.includes("one_to_one")) return "one_to_one";
  if (normalized.includes("many_to_many")) return "many_to_many";
  return "one_to_many";
};

const ORDERS_USED_IN: KgDataSourceUsedInResponse["sections"] = [
  {
    key: "ontologies",
    label: "Ontologies",
    items: [usedInApiItem("ont-order", "Order")],
    pageInfo: emptyKgPageInfo(1),
  },
  {
    key: "stories",
    label: "Data stories",
    items: [
      usedInApiItem("dstory-q3", "Q3 revenue review"),
      usedInApiItem("dstory-cohort", "Customer cohort health"),
      usedInApiItem("dstory-returns", "Returns deep dive"),
      usedInApiItem("dstory-margin", "Margin watch"),
    ],
    pageInfo: emptyKgPageInfo(4),
  },
];

const previewBadge = (
  kind: KgTablePreviewRow["badge"]["kind"],
  tone: KgTablePreviewRow["badge"]["tone"],
): KgTablePreviewRow["badge"] => ({
  kind,
  label: kind,
  tone,
});

const ORDERS_TABLE_PREVIEW_ROWS: KgTablePreviewRow[] = [
  {
    id: "col-order-id",
    name: "order_id",
    badge: previewBadge("PK", "amber"),
  },
  {
    id: "col-customer-id",
    name: "customer_id",
    badge: previewBadge("FK", "blue"),
  },
  {
    id: "col-order-date",
    name: "order_date",
    badge: previewBadge("DIM", "gray"),
  },
  {
    id: "col-ship-mode",
    name: "ship_mode",
    badge: previewBadge("DIM", "gray"),
  },
  {
    id: "col-region-id",
    name: "region_id",
    badge: previewBadge("FK", "blue"),
  },
  {
    id: "col-order-total",
    name: "order_total",
    badge: previewBadge("MS", "gray"),
  },
  {
    id: "col-order-status",
    name: "order_status",
    badge: previewBadge("DIM", "gray"),
  },
];

const findUpstreamDataSource = (tableId: string): KgFlowNode | undefined => {
  const edge = sampleEdges.find((item) => item.target === tableId);
  if (!edge) return undefined;
  const source = findNode(edge.source);
  return source?.type === "dataSource" ? source : undefined;
};

const buildTableOverviewResponse = (
  node: KgFlowNode,
): KgTableOverviewResponse => {
  const dataSource = findUpstreamDataSource(node.id);
  const details = node.data.details ?? dataSource?.data.details;
  const previewRows =
    node.id === "tbl-orders"
      ? ORDERS_TABLE_PREVIEW_ROWS
      : (node.data.fields ?? []).map((field, index) => ({
          id: `col-${index}`,
          name: field.name,
          badge: previewBadge(field.keyType ?? "DIM", "gray"),
        }));

  return {
    id: node.id,
    name: node.data.title ?? node.id,
    icon: "table",
    externalUrl: details?.externalUrl,
    fields: [
      {
        key: "source",
        label: "Source",
        valueType: "ref",
        value: {
          id: dataSource?.id ?? "ds-unknown",
          type: "dataSource",
          name: dataSource?.data.title ?? "—",
          icon: dataSource?.data.logoUrl ?? "snowflake",
          externalUrl: details?.externalUrl,
          href: dataSource?.id ? `/graph?focus=${dataSource.id}` : undefined,
        },
      },
    ],
    tablePreview: {
      label: "Columns",
      rows: previewRows,
      total: previewRows.length,
    },
  };
};

export const mockFetchTableOverview = async (
  schemaId: string,
): Promise<KgTableOverviewResponse> => {
  await delay();
  const node = findNode(schemaId);
  if (!node || node.type !== "table") {
    throw new Error(`Table schema not found: ${schemaId}`);
  }
  return buildTableOverviewResponse(node);
};

export const mockFetchNodeOverview = async (
  nodeId: string,
): Promise<KgNodeOverviewResponse> => {
  await delay();
  const node = findNode(nodeId);
  if (!node) {
    throw new Error(`Node not found: ${nodeId}`);
  }

  if (node.type === "table") {
    throw new Error(
      `Use fetchTableOverview for table nodes (schemaId: ${nodeId})`,
    );
  }

  const details = node.data.details;
  return {
    id: node.id,
    name: node.data.title ?? node.id,
    icon: node.data.logoUrl,
    externalUrl: details?.externalUrl,
    fields: buildOverviewFields(details),
  };
};

export const mockFetchDataSourceTables = async (
  dataSourceId: string,
  _params?: KgDrawerListParams,
): Promise<KgDataSourceTablesResponse> => {
  await delay();
  const node = findNode(dataSourceId);
  if (!node || node.type !== "dataSource") {
    throw new Error(`Data source not found: ${dataSourceId}`);
  }

  const details = node.data.details;
  if (details?.tables?.length) {
    const rows = details.tables.map((row, index) => ({
      id: `tbl-mock-${index}`,
      name:
        // prefer explicit tableName where available, fall back to any common alternatives
        (row as any).tableName ??
        (row as any).name ??
        (row as any).title ??
        `tbl-mock-${index}`,
      primaryKey: row.primaryKey,
    }));
    return { rows, pageInfo: emptyKgPageInfo(rows.length) };
  }

  return deriveTablesFromGraph(dataSourceId);
};

export const mockFetchDataSourceReports = async (
  dataSourceId: string,
  _params?: KgDrawerListParams,
): Promise<KgDataSourceReportsResponse> => {
  await delay();
  const node = findNode(dataSourceId);
  if (!node || node.type !== "dataSource") {
    throw new Error(`Data source not found: ${dataSourceId}`);
  }

  const details = node.data.details;
  if (details?.reports?.length) {
    const rows = details.reports.map((row, index) => ({
      id: `rpt-mock-${index}`,
      title: row.title,
      connectedOn: row.connectedOn,
    }));
    return { rows, pageInfo: emptyKgPageInfo(rows.length) };
  }

  return deriveReportsFromGraph(dataSourceId, details);
};

export const mockFetchDataSourceUsedIn = async (
  dataSourceId: string,
): Promise<KgDataSourceUsedInResponse> => {
  await delay();
  const node = findNode(dataSourceId);
  if (!node || node.type !== "dataSource") {
    throw new Error(`Data source not found: ${dataSourceId}`);
  }

  const details = node.data.details;
  if (details?.usedInSections?.length) {
    const sections = details.usedInSections.map((section) => ({
      key: (section.id === "dataStories"
        ? "stories"
        : section.id === "reports"
          ? "reports"
          : "ontologies") as KgUsedInSectionKey,
      label: section.label,
      items: section.items.map((item, index) =>
        resolveUsedInItemFromGraph(
          dataSourceId,
          section.nodeType,
          item.label,
          `${section.id}-${index}`,
        ),
      ),
      pageInfo: emptyKgPageInfo(section.items.length),
    }));
    return { sections };
  }

  return deriveUsedInFromGraph(dataSourceId);
};

const deriveSchemaRelationshipsFromGraph = (
  schemaId: string,
): KgSchemaRelationshipsResponse => {
  if (schemaId === "tbl-orders") {
    return { rows: ORDERS_RELATIONSHIPS, pageInfo: emptyKgPageInfo(5) };
  }
  return { rows: [], pageInfo: emptyKgPageInfo(0) };
};

const collectUpstreamMetricIds = (tableId: string): string[] => {
  const ontologyIds = sampleEdges
    .filter((edge) => edge.source === tableId)
    .map((edge) => edge.target);
  const metricIds = new Set<string>();
  for (const ontologyId of ontologyIds) {
    for (const edge of sampleEdges) {
      if (edge.source === ontologyId) {
        const node = findNode(edge.target);
        if (node?.type === "metric") {
          metricIds.add(edge.target);
        }
      }
    }
  }
  return Array.from(metricIds);
};

const deriveSchemaMetricsFromGraph = (
  schemaId: string,
): KgSchemaMetricsResponse => {
  if (schemaId === "tbl-orders") {
    return { items: ORDERS_METRICS, pageInfo: emptyKgPageInfo(4) };
  }

  const items = collectUpstreamMetricIds(schemaId).map((id) => {
    const node = findNode(id);
    return {
      id,
      name: node?.data.title ?? id,
      href: MOCK_EXTERNAL_LINK,
      externalUrl: null,
    };
  });

  return { items, pageInfo: emptyKgPageInfo(items.length) };
};

const deriveSchemaUsedInFromGraph = (
  schemaId: string,
): KgDataSourceUsedInResponse => {
  if (schemaId === "tbl-orders") {
    return { sections: ORDERS_USED_IN };
  }

  const ontologyItems = sampleEdges
    .filter((edge) => edge.source === schemaId)
    .map((edge) => findNode(edge.target))
    .filter((node): node is KgFlowNode => node?.type === "ontology")
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );

  const descendantIds = collectDescendantIds(schemaId, sampleEdges);
  const storyItems = sampleNodes
    .filter((node) => node.type === "dataStory" && descendantIds.has(node.id))
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );

  const sections: KgDataSourceUsedInResponse["sections"] = [];

  if (ontologyItems.length > 0) {
    sections.push({
      key: "ontologies",
      label: "Ontologies",
      items: ontologyItems,
      pageInfo: emptyKgPageInfo(ontologyItems.length),
    });
  }

  if (storyItems.length > 0) {
    sections.push({
      key: "stories",
      label: "Data stories",
      items: storyItems,
      pageInfo: emptyKgPageInfo(storyItems.length),
    });
  }

  return { sections };
};

export const mockFetchSchemaRelationships = async (
  schemaId: string,
  _params?: KgSchemaRelationshipsParams,
): Promise<KgSchemaRelationshipsResponse> => {
  await delay();
  const node = findNode(schemaId);
  if (!node || node.type !== "table") {
    throw new Error(`Schema not found: ${schemaId}`);
  }

  const details = node.data.details;
  if (details?.relationships?.length) {
    const rows = details.relationships.map((row, index) => ({
      id: row.id ?? `rel-mock-${index}`,
      name: (row as any).tableName ?? (row as any).name ?? "—",
      nameHref:
        (row as any).nameHref ?? (row as any).tableHref ?? MOCK_EXTERNAL_LINK,
      column: row.columnName,
      relationType: relationTypeFromLabel(row.relationType),
      relationLabel: row.relationType,
    }));
    return { rows, pageInfo: emptyKgPageInfo(rows.length) };
  }

  return deriveSchemaRelationshipsFromGraph(schemaId);
};

export const mockFetchSchemaMetrics = async (
  schemaId: string,
  _params?: KgDrawerListParams,
): Promise<KgSchemaMetricsResponse> => {
  await delay();
  const node = findNode(schemaId);
  if (!node || node.type !== "table") {
    throw new Error(`Schema not found: ${schemaId}`);
  }

  const details = node.data.details;
  if (details?.metrics?.length) {
    const items = details.metrics.map((row, index) => ({
      id: `metric-mock-${index}`,
      name: row.label,
      href: row.externalUrl ?? MOCK_EXTERNAL_LINK,
      externalUrl: row.externalUrl ?? null,
    }));
    return { items, pageInfo: emptyKgPageInfo(items.length) };
  }

  return deriveSchemaMetricsFromGraph(schemaId);
};

export const mockFetchSchemaUsedIn = async (
  schemaId: string,
): Promise<KgDataSourceUsedInResponse> => {
  await delay();
  const node = findNode(schemaId);
  if (!node || node.type !== "table") {
    throw new Error(`Schema not found: ${schemaId}`);
  }

  const details = node.data.details;
  if (details?.usedInSections?.length) {
    const sections = details.usedInSections.map((section) => ({
      key: (section.id === "dataStories"
        ? "stories"
        : section.id === "reports"
          ? "reports"
          : "ontologies") as KgUsedInSectionKey,
      label: section.label,
      items: section.items.map((item, index) =>
        resolveUsedInItemFromGraph(
          schemaId,
          section.nodeType,
          item.label,
          `${section.id}-${index}`,
        ),
      ),
      pageInfo: emptyKgPageInfo(section.items.length),
    }));
    return { sections };
  }

  return deriveSchemaUsedInFromGraph(schemaId);
};

// ─── Ontology drawer mocks (Figma-aligned for ont-batch) ─────────────────────

const BATCH_OVERVIEW: KgOntologyOverviewResponse = {
  id: "ont-batch",
  name: "Batch",
  externalUrl: MOCK_EXTERNAL_LINK,
  description:
    "A batch represents a group of related orders processed together in a single fulfillment cycle.",
  dataSources: [
    {
      id: "ds-orders",
      tableName: "orders",
      dataSourceName: "Snowflake",
      externalUrl: MOCK_EXTERNAL_LINK,
    },
    {
      id: "ds-order-items",
      tableName: "order_items",
      dataSourceName: "Snowflake",
      externalUrl: MOCK_EXTERNAL_LINK,
    },
    {
      id: "ds-shipments",
      tableName: "shipments",
      dataSourceName: "Snowflake",
      externalUrl: MOCK_EXTERNAL_LINK,
    },
  ],
};

const focusHref = (canvasNodeId: string) => `/graph?focus=${canvasNodeId}`;

const BATCH_LINKS: KgOntologyLinksResponse = {
  source: {
    id: "ont-batch",
    name: "Batch",
    href: focusHref("ont-batch"),
  },
  rows: [
    {
      id: "link-1",
      source: "Batch",
      verb: "placed by",
      target: "Customer",
      targetHref: focusHref("ont-customer"),
    },
    {
      id: "link-2",
      source: "Product",
      sourceHref: focusHref("ont-product"),
      verb: "contains",
      target: "Batch",
      targetHref: focusHref("ont-batch"),
    },
    {
      id: "link-3",
      source: "Batch",
      verb: "ships to",
      target: "Region",
      targetHref: focusHref("ont-region"),
    },
    {
      id: "link-4",
      source: "Batch",
      verb: "may have",
      target: "Return",
      targetHref: focusHref("ont-product"),
    },
  ],
  pageInfo: emptyKgPageInfo(4),
};

const BATCH_METRICS: KgOntologyMetricsResponse = {
  items: [
    {
      id: "m-gross-sales",
      name: "Gross sales ($)",
      href: focusHref("m-gross-sales"),
    },
    {
      id: "m-avg-order",
      name: "Avg order value",
      href: focusHref("m-avg-order"),
    },
    {
      id: "m-order-count",
      name: "Order count",
      href: focusHref("m-order-count"),
    },
  ],
  pageInfo: emptyKgPageInfo(3),
};

const BATCH_RULES: KgOntologyRulesResponse = {
  sections: [
    {
      key: "businessRules",
      label: "Business rules",
      items: [
        {
          id: "br-1",
          name: "Exclude canceled orders from revenue",
          externalUrl: MOCK_EXTERNAL_LINK,
        },
        {
          id: "br-2",
          name: "Same day shipping ≠ ship_mode...",
          externalUrl: MOCK_EXTERNAL_LINK,
        },
        {
          id: "br-3",
          name: "Fiscal year Feb-Jan",
          externalUrl: MOCK_EXTERNAL_LINK,
        },
      ],
      pageInfo: emptyKgPageInfo(3),
    },
    {
      key: "tribalKnowledge",
      label: "Tribal knowledge",
      items: [
        {
          id: "tk-1",
          name: "30 day return window",
          externalUrl: MOCK_EXTERNAL_LINK,
        },
      ],
      pageInfo: emptyKgPageInfo(1),
    },
  ],
};

const BATCH_REPORTS: KgOntologyReportsResponse = {
  rows: [
    { id: "r-1", title: "Sales trend dashboard", dataSource: "Tableau" },
    { id: "r-2", title: "Margin detail", dataSource: "Power Bi" },
    { id: "r-3", title: "Gross sales KPI", dataSource: "Magic Bi" },
    { id: "r-4", title: "Avg order value trend", dataSource: "Magic Bi" },
    { id: "r-5", title: "Order volume bar", dataSource: "Magic Bi" },
  ],
  pageInfo: emptyKgPageInfo(5),
};

const deriveOntologyMetricsFromGraph = (
  ontologyId: string,
): KgOntologyMetricsResponse => {
  const items = sampleEdges
    .filter((edge) => edge.source === ontologyId)
    .map((edge) => findNode(edge.target))
    .filter((node): node is KgFlowNode => node?.type === "metric")
    .map((node) => ({
      id: node.id,
      name: node.data.title ?? node.id,
      externalUrl: MOCK_EXTERNAL_LINK,
    }));

  return { items, pageInfo: emptyKgPageInfo(items.length) };
};

const deriveOntologyUsedInFromGraph = (
  ontologyId: string,
): KgDataSourceUsedInResponse => {
  const descendantIds = collectDescendantIds(ontologyId, sampleEdges);

  const reportItems = sampleNodes
    .filter((node) => node.type === "report" && descendantIds.has(node.id))
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );

  const storyItems = sampleNodes
    .filter((node) => node.type === "dataStory" && descendantIds.has(node.id))
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );

  const sections: KgDataSourceUsedInResponse["sections"] = [];

  if (reportItems.length > 0) {
    sections.push({
      key: "reports",
      label: "Reports",
      items: reportItems,
      pageInfo: emptyKgPageInfo(reportItems.length),
    });
  }

  if (storyItems.length > 0) {
    sections.push({
      key: "stories",
      label: "Data stories",
      items: storyItems,
      pageInfo: emptyKgPageInfo(storyItems.length),
    });
  }

  return { sections };
};

export const mockFetchOntologyOverview = async (
  ontologyId: string,
): Promise<KgOntologyOverviewResponse> => {
  await delay();
  const node = findNode(ontologyId);
  if (!node || node.type !== "ontology") {
    throw new Error(`Ontology not found: ${ontologyId}`);
  }

  if (ontologyId === "ont-batch") {
    return { ...BATCH_OVERVIEW, name: node.data.title ?? BATCH_OVERVIEW.name };
  }

  return {
    id: ontologyId,
    name: node.data.title ?? ontologyId,
    externalUrl: node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
    description: `Ontology definition for ${node.data.title ?? ontologyId}.`,
    dataSources: [],
  };
};

export const mockFetchDataModelOverview = async (
  dataModelId: string,
): Promise<KgDataModelOverviewResponse> => {
  await delay();
  // The canvas may run against the live graph API while the drawer is mocked,
  // so real workspace IDs won't exist in the sample graph. Render a generic
  // overview for unknown IDs instead of failing the tab.
  const node = findNode(dataModelId);
  const isSampleDataModel = node?.type === "dataModel";

  const name = (isSampleDataModel && node.data.title) || "Data model";
  return {
    id: dataModelId,
    name,
    externalUrl:
      (isSampleDataModel && node.data.details?.externalUrl) ||
      `https://example.com/data-models/${dataModelId}`,
    description: `A batch represents a group of related ${name.toLowerCase()}s processed together in a single fulfillment cycle.`,
  };
};

/** Figma 2745-38585 demo rows — used when the id isn't in the sample graph. */
const DEMO_DATA_MODEL_TABLE_ROWS = [
  { id: "tbl-demo-orders", name: "orders", primaryKey: "order_id" },
  { id: "tbl-demo-products", name: "products", primaryKey: "product_id" },
  { id: "tbl-demo-customers", name: "customers", primaryKey: "customer_id" },
];

/** Sample graph links tables INTO a data model (table → dataModel edges). */
const deriveDataModelTablesFromGraph = (
  dataModelId: string,
): KgDataModelTablesResponse => {
  const tableIds = new Set(
    sampleEdges
      .filter((edge) => edge.target === dataModelId)
      .map((edge) => edge.source),
  );

  const rows = sampleNodes
    .filter((node) => node.type === "table" && tableIds.has(node.id))
    .map((node) => {
      const pk =
        node.data.fields?.find((field) => field.keyType === "PK")?.name ?? "—";
      return {
        id: node.id,
        name: toTableSlug(node.data.title ?? node.id),
        primaryKey: pk,
      };
    });

  return { rows, pageInfo: emptyKgPageInfo(rows.length) };
};

export const mockFetchDataModelTables = async (
  dataModelId: string,
  _params?: KgDrawerListParams,
): Promise<KgDataModelTablesResponse> => {
  await delay();
  // Same fallback policy as the data-model overview: the canvas may run live
  // while the drawer is mocked, so unknown ids render demo rows, not an error.
  const node = findNode(dataModelId);
  if (node?.type === "dataModel") {
    const derived = deriveDataModelTablesFromGraph(dataModelId);
    if (derived.rows.length > 0) return derived;
  }

  return {
    rows: DEMO_DATA_MODEL_TABLE_ROWS,
    pageInfo: emptyKgPageInfo(DEMO_DATA_MODEL_TABLE_ROWS.length),
  };
};

/** Figma 2745-38675 demo metrics — used when the id isn't in the sample graph. */
const DEMO_DATA_MODEL_METRIC_ITEMS = [
  { id: "met-demo-gross-sales", name: "Gross sales ($)" },
  { id: "met-demo-avg-order-value", name: "Avg order value" },
  { id: "met-demo-order-count", name: "Order count" },
  { id: "met-demo-regional-share", name: "Regional sales share (%)" },
].map((item) => ({ ...item, externalUrl: MOCK_EXTERNAL_LINK }));

/** Data models reach metrics downstream (dm → ontology → metric). */
const deriveDataModelMetricsFromGraph = (
  dataModelId: string,
): KgDataModelMetricsResponse => {
  const descendantIds = collectDescendantIds(dataModelId, sampleEdges);

  const items = sampleNodes
    .filter((node) => node.type === "metric" && descendantIds.has(node.id))
    .map((node) => ({
      id: node.id,
      name: node.data.title ?? node.id,
      externalUrl: MOCK_EXTERNAL_LINK,
    }));

  return { items, pageInfo: emptyKgPageInfo(items.length) };
};

export const mockFetchDataModelMetrics = async (
  dataModelId: string,
  _params?: KgDrawerListParams,
): Promise<KgDataModelMetricsResponse> => {
  await delay();
  // Same fallback policy as the data-model overview/tables mocks.
  const node = findNode(dataModelId);
  if (node?.type === "dataModel") {
    const derived = deriveDataModelMetricsFromGraph(dataModelId);
    if (derived.items.length > 0) return derived;
  }

  return {
    items: DEMO_DATA_MODEL_METRIC_ITEMS,
    pageInfo: emptyKgPageInfo(DEMO_DATA_MODEL_METRIC_ITEMS.length),
  };
};

/** Figma 2745-38725 demo relationship rows (two-line cells). */
const DEMO_DATA_MODEL_RELATIONSHIP_ROWS = [
  {
    id: "rel-dm-demo-1",
    fromTable: "Order",
    fromColumn: "Order_item",
    relationType: "Right",
    cardinality: "one to many",
    toTable: "Product",
    toColumn: "Product_id",
  },
  {
    id: "rel-dm-demo-2",
    fromTable: "Order",
    fromColumn: "Date",
    relationType: "Inner",
    cardinality: "many to one",
    toTable: "Customer",
    toColumn: "Customer_id",
  },
  {
    id: "rel-dm-demo-3",
    fromTable: "Customer",
    fromColumn: "Customer_name",
    relationType: "Full",
    cardinality: "one to one",
    toTable: "Product",
    toColumn: "Product_name",
  },
];

export const mockFetchDataModelRelationships = async (
  _dataModelId: string,
  _params?: KgDrawerListParams,
): Promise<KgDataModelRelationshipsResponse> => {
  await delay();
  // The sample graph carries no column-level relation data for data models, so
  // every id gets the Figma demo rows (same never-throw policy as the other
  // data-model mocks — the canvas may run live while the drawer is mocked).
  return {
    rows: DEMO_DATA_MODEL_RELATIONSHIP_ROWS,
    pageInfo: emptyKgPageInfo(DEMO_DATA_MODEL_RELATIONSHIP_ROWS.length),
  };
};

export const mockFetchOntologyLinks = async (
  ontologyId: string,
  _params?: KgDrawerListParams,
): Promise<KgOntologyLinksResponse> => {
  await delay();
  const node = findNode(ontologyId);
  if (!node || node.type !== "ontology") {
    throw new Error(`Ontology not found: ${ontologyId}`);
  }

  if (ontologyId === "ont-batch") {
    return BATCH_LINKS;
  }

  return { rows: [], pageInfo: emptyKgPageInfo(0) };
};

export const mockFetchOntologyMetrics = async (
  ontologyId: string,
  _params?: KgDrawerListParams,
): Promise<KgOntologyMetricsResponse> => {
  await delay();
  const node = findNode(ontologyId);
  if (!node || node.type !== "ontology") {
    throw new Error(`Ontology not found: ${ontologyId}`);
  }

  if (ontologyId === "ont-batch") {
    return BATCH_METRICS;
  }

  return deriveOntologyMetricsFromGraph(ontologyId);
};

export const mockFetchOntologyRules = async (
  ontologyId: string,
): Promise<KgOntologyRulesResponse> => {
  await delay();
  const node = findNode(ontologyId);
  if (!node || node.type !== "ontology") {
    throw new Error(`Ontology not found: ${ontologyId}`);
  }

  if (ontologyId === "ont-batch") {
    return BATCH_RULES;
  }

  return { sections: [] };
};

export const mockFetchOntologyReports = async (
  ontologyId: string,
  _params?: KgDrawerListParams,
): Promise<KgOntologyReportsResponse> => {
  await delay();
  const node = findNode(ontologyId);
  if (!node || node.type !== "ontology") {
    throw new Error(`Ontology not found: ${ontologyId}`);
  }

  if (ontologyId === "ont-batch") {
    return BATCH_REPORTS;
  }

  return { rows: [], pageInfo: emptyKgPageInfo(0) };
};

export const mockFetchOntologyUsedIn = async (
  ontologyId: string,
): Promise<KgDataSourceUsedInResponse> => {
  await delay();
  const node = findNode(ontologyId);
  if (!node || node.type !== "ontology") {
    throw new Error(`Ontology not found: ${ontologyId}`);
  }

  if (ontologyId === "ont-batch") {
    return deriveOntologyUsedInFromGraph(ontologyId);
  }

  return deriveOntologyUsedInFromGraph(ontologyId);
};

// ─── Metric drawer mocks (Figma-aligned for m-gm) ───────────────────────────

const GM_OVERVIEW: KgMetricOverviewResponse = {
  id: "m-gm",
  name: "Gross margin (%)",
  externalUrl: MOCK_EXTERNAL_LINK,
  description: "Daily and weekly gross sales by region.",
  verificationStatus: "verified",
  verifiedBy: "Sarah Chen",
  lastUpdatedBy: "Sarah Chen",
  updatedOn: "1 hour ago",
  tags: ["Order", "Batch"],
  ontologies: [
    { id: "ont-order", name: "Order", externalUrl: MOCK_EXTERNAL_LINK },
    { id: "ont-batch", name: "Batch", externalUrl: MOCK_EXTERNAL_LINK },
  ],
};

const GM_FORMULA: KgMetricFormulaResponse = {
  expression: "(SUM(line_total) - SUM(cost_amount)) / SUM(line_total) * 100",
  columns: [
    {
      id: "col-line-total",
      columnName: "line_total",
      tableName: "order_items",
      dataSourceName: "Snowflake",
    },
    {
      id: "col-cost-amount",
      columnName: "cost_amount",
      tableName: "order_items",
      dataSourceName: "Snowflake",
    },
  ],
};

const collectParentOntologyIds = (metricId: string): string[] =>
  sampleEdges
    .filter((edge) => edge.target === metricId)
    .map((edge) => edge.source)
    .filter((sourceId) => findNode(sourceId)?.type === "ontology");

const deriveMetricOverviewFromGraph = (
  metricId: string,
): KgMetricOverviewResponse => {
  const node = findNode(metricId);
  const ontologyIds = collectParentOntologyIds(metricId);
  const ontologies = ontologyIds.map((ontologyId) => {
    const ontology = findNode(ontologyId);
    return {
      id: ontologyId,
      name: ontology?.data.title ?? ontologyId,
      externalUrl: ontology?.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
    };
  });

  return {
    id: metricId,
    name: node?.data.title ?? metricId,
    externalUrl: node?.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
    description: `Metric definition for ${node?.data.title ?? metricId}.`,
    verificationStatus: "verified",
    verifiedBy: "Sarah Chen",
    lastUpdatedBy: "Sarah Chen",
    updatedOn: "1 hour ago",
    tags: ontologies.map((ontology) => ontology.name),
    ontologies,
  };
};

const GM_USED_IN: KgDataSourceUsedInResponse = {
  sections: [
    {
      key: "reports",
      label: "Reports",
      items: [
        usedInApiItem("rpt-margin-detail", "Margin detail (Power BI)"),
        usedInApiItem("rpt-discount", "Discount performance (Power BI)"),
        usedInApiItem("rpt-margin-cat", "Margin by category (Magic BI)"),
      ],
      pageInfo: emptyKgPageInfo(3),
    },
    {
      key: "stories",
      label: "Data stories",
      items: [
        usedInApiItem("dstory-margin", "Margin watch"),
        usedInApiItem("dstory-promo", "Promo campaign wrap up"),
      ],
      pageInfo: emptyKgPageInfo(2),
    },
  ],
};

const deriveMetricUsedInFromGraph = (
  metricId: string,
): KgDataSourceUsedInResponse => {
  const reportItems = sampleEdges
    .filter((edge) => edge.source === metricId)
    .map((edge) => findNode(edge.target))
    .filter((node): node is KgFlowNode => node?.type === "report")
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );

  const storyItems = sampleEdges
    .filter((edge) => edge.source === metricId)
    .map((edge) => findNode(edge.target))
    .filter((node): node is KgFlowNode => node?.type === "dataStory")
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );

  const sections: KgDataSourceUsedInResponse["sections"] = [];

  if (reportItems.length > 0) {
    sections.push({
      key: "reports",
      label: "Reports",
      items: reportItems,
      pageInfo: emptyKgPageInfo(reportItems.length),
    });
  }

  if (storyItems.length > 0) {
    sections.push({
      key: "stories",
      label: "Data stories",
      items: storyItems,
      pageInfo: emptyKgPageInfo(storyItems.length),
    });
  }

  return { sections };
};

export const mockFetchMetricOverview = async (
  metricId: string,
): Promise<KgMetricOverviewResponse> => {
  await delay();
  const node = findNode(metricId);
  if (!node || node.type !== "metric") {
    throw new Error(`Metric not found: ${metricId}`);
  }

  if (metricId === "m-gm") {
    return { ...GM_OVERVIEW, name: node.data.title ?? GM_OVERVIEW.name };
  }

  return deriveMetricOverviewFromGraph(metricId);
};

export const mockFetchMetricFormula = async (
  metricId: string,
): Promise<KgMetricFormulaResponse> => {
  await delay();
  const node = findNode(metricId);
  if (!node || node.type !== "metric") {
    throw new Error(`Metric not found: ${metricId}`);
  }

  if (metricId === "m-gm") {
    return GM_FORMULA;
  }

  return {
    expression: "",
    columns: [],
  };
};

export const mockFetchMetricUsedIn = async (
  metricId: string,
): Promise<KgDataSourceUsedInResponse> => {
  await delay();
  const node = findNode(metricId);
  if (!node || node.type !== "metric") {
    throw new Error(`Metric not found: ${metricId}`);
  }

  if (metricId === "m-gm") {
    return GM_USED_IN;
  }

  return deriveMetricUsedInFromGraph(metricId);
};

// ─── Report drawer mocks (Figma-aligned for rpt-trend) ────────────────────────

const inferReportSourceType = (biName?: string): KgReportSourceType => {
  if (biName === "MagicBI") return "magicbi";
  if (biName === "Confluence" || biName === "Jira") return "retrieved";
  return "datasource";
};

const RPT_TREND_OVERVIEW: KgReportOverviewResponse = {
  id: "rpt-trend",
  name: "Sales trend dashboard",
  sourceType: "datasource",
  externalUrl: MOCK_EXTERNAL_LINK,
  description: "Daily and weekly gross sales by region.",
  source: {
    name: "Tableau",
    logoUrl: "/logos/tableau.svg",
    externalUrl: MOCK_EXTERNAL_LINK,
  },
  reportType: "Dashboard",
  fetchedOn: "1 hour ago",
};

const RPT_TREND_TRACE: KgReportTraceResponse = {
  sections: [
    {
      key: "dataSources",
      label: "Data sources",
      items: [usedInApiItem("ds-snowflake", "Snowflake")],
      pageInfo: emptyKgPageInfo(1),
    },
    {
      key: "tables",
      label: "Tables",
      items: [
        usedInApiItem("tbl-sales-report", "sales_report"),
        usedInApiItem("tbl-inventory", "inventory"),
      ],
      pageInfo: emptyKgPageInfo(2),
    },
    {
      key: "ontologies",
      label: "Ontologies",
      items: [
        usedInApiItem("ont-batch", "Batch"),
        usedInApiItem("ont-region", "Region"),
      ],
      pageInfo: emptyKgPageInfo(2),
    },
    {
      key: "metrics",
      label: "Metrics",
      items: [
        usedInApiItem("m-sales-velocity", "Sales velocity"),
        usedInApiItem("m-rev-growth", "Revenue growth"),
      ],
      pageInfo: emptyKgPageInfo(2),
    },
  ],
};

const RPT_TREND_USED_IN: KgDataSourceUsedInResponse = {
  sections: [
    {
      key: "stories",
      label: "Data stories",
      items: [usedInApiItem("dstory-q3", "Q3 revenue report")],
      pageInfo: emptyKgPageInfo(1),
    },
  ],
};

const RPT_RETURNS_OVERVIEW: KgReportOverviewResponse = {
  id: "rpt-returns",
  name: "Return policy v3.2",
  sourceType: "retrieved",
  externalUrl: MOCK_EXTERNAL_LINK,
  description: "30-day window, restocking fees, exclusions.",
  source: {
    name: "Confluence",
    logoUrl: "/logos/confluence.svg",
    externalUrl: MOCK_EXTERNAL_LINK,
  },
  reportType: "Policy",
  fetchedOn: "6 days ago",
};

const RPT_RETURNS_TRACE: KgReportTraceResponse = {
  sections: [
    {
      key: "ontologies",
      label: "Ontologies",
      items: [usedInApiItem("ont-product", "Return")],
      pageInfo: emptyKgPageInfo(1),
    },
    {
      key: "metrics",
      label: "Metrics",
      items: [usedInApiItem("m-return-rate", "Return rate")],
      pageInfo: emptyKgPageInfo(1),
    },
  ],
};

const RPT_RETURNS_USED_IN: KgDataSourceUsedInResponse = {
  sections: [
    {
      key: "stories",
      label: "Data stories",
      items: [usedInApiItem("dstory-q3", "Returns deep dive")],
      pageInfo: emptyKgPageInfo(1),
    },
  ],
};

/** Figma native report — `rpt-gross` (Gross sales KPI). */
const RPT_GROSS_OVERVIEW: KgReportOverviewResponse = {
  id: "rpt-gross",
  name: "Gross sales KPI",
  sourceType: "magicbi",
  externalUrl: MOCK_EXTERNAL_LINK,
  description: "KPI tile of gross sales for the period.",
  source: {
    name: "Magic BI",
    logoUrl: "/logos/magicbi.svg",
    externalUrl: MOCK_EXTERNAL_LINK,
  },
  reportType: "Chart - KPI",
  updatedOn: "Live",
  updatedBy: "Magic BI system",
};

const RPT_GROSS_TRACE: KgReportTraceResponse = {
  sections: [
    {
      key: "ontologies",
      label: "Ontologies",
      items: [usedInApiItem("ont-order", "Order")],
      pageInfo: emptyKgPageInfo(1),
    },
    {
      key: "metrics",
      label: "Metrics",
      items: [usedInApiItem("m-gross-sales", "Gross sales (%)")],
      pageInfo: emptyKgPageInfo(1),
    },
  ],
};

const RPT_GROSS_USED_IN: KgDataSourceUsedInResponse = {
  sections: [
    {
      key: "stories",
      label: "Data stories",
      items: [usedInApiItem("dstory-q3", "Q3 revenue review")],
      pageInfo: emptyKgPageInfo(1),
    },
  ],
};

const RPT_GROSS_ACTIVITY: KgReportActivityResponse = {
  versions: [
    {
      id: "v1",
      version: "V1",
      lastUpdated: "Live",
      updatedBy: "Magic BI system",
    },
  ],
};

const TRACE_SECTION_LABELS: Record<KgTraceSectionKey, string> = {
  dataSources: "Data sources",
  tables: "Tables",
  ontologies: "Ontologies",
  metrics: "Metrics",
};

const deriveReportOverviewFromGraph = (
  reportId: string,
): KgReportOverviewResponse => {
  const node = findNode(reportId);
  if (!node || node.type !== "report") {
    throw new Error(`Report not found: ${reportId}`);
  }

  const biName = node.data.biName;
  const sourceType =
    node.data.reportSourceType ?? inferReportSourceType(biName);

  return {
    id: reportId,
    name: node.data.title ?? reportId,
    sourceType,
    externalUrl: node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
    description: "",
    source:
      (sourceType === "datasource" || sourceType === "retrieved") && biName
        ? {
            name: biName,
            logoUrl: node.data.biLogoUrl,
            externalUrl: MOCK_EXTERNAL_LINK,
          }
        : undefined,
    reportType:
      sourceType === "datasource"
        ? "Dashboard"
        : sourceType === "retrieved"
          ? "Policy"
          : undefined,
    fetchedOn:
      sourceType === "datasource"
        ? "1 hour ago"
        : sourceType === "retrieved"
          ? "6 days ago"
          : undefined,
    ...(sourceType === "magicbi"
      ? {
          source: {
            name: "Magic BI",
            logoUrl: "/logos/magicbi.svg",
            externalUrl: MOCK_EXTERNAL_LINK,
          },
          reportType: "Chart - KPI",
          updatedOn: "Live",
          updatedBy: "Magic BI system",
        }
      : {}),
  };
};

const collectUpstreamByType = (
  reportId: string,
  nodeType: KgFlowNode["type"],
): ReturnType<typeof usedInApiItem>[] => {
  const direct = sampleEdges
    .filter((edge) => edge.target === reportId)
    .map((edge) => findNode(edge.source))
    .filter((node): node is KgFlowNode => node?.type === nodeType);

  const fromMetrics =
    nodeType === "ontology"
      ? sampleEdges
          .filter((edge) => {
            const targetNode = findNode(edge.target);
            return (
              targetNode?.type === "metric" &&
              sampleEdges.some(
                (metricEdge) =>
                  metricEdge.source === targetNode.id &&
                  metricEdge.target === reportId,
              )
            );
          })
          .map((edge) => findNode(edge.source))
          .filter((node): node is KgFlowNode => node?.type === "ontology")
      : [];

  const nodes = [...direct, ...fromMetrics];
  const seen = new Set<string>();

  return nodes
    .filter((node) => {
      if (seen.has(node.id)) return false;
      seen.add(node.id);
      return true;
    })
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );
};

const deriveReportTraceFromGraph = (
  reportId: string,
): KgReportTraceResponse => {
  const keys: KgTraceSectionKey[] = [
    "dataSources",
    "tables",
    "ontologies",
    "metrics",
  ];

  const nodeTypeByKey: Record<KgTraceSectionKey, KgFlowNode["type"]> = {
    dataSources: "dataSource",
    tables: "table",
    ontologies: "ontology",
    metrics: "metric",
  };

  const sections = keys
    .map((key) => {
      const items = collectUpstreamByType(reportId, nodeTypeByKey[key]);
      return {
        key,
        label: TRACE_SECTION_LABELS[key],
        items,
        pageInfo: emptyKgPageInfo(items.length),
      };
    })
    .filter((section) => section.items.length > 0);

  return { sections };
};

const deriveReportUsedInFromGraph = (
  reportId: string,
): KgDataSourceUsedInResponse => {
  const storyItems = sampleEdges
    .filter((edge) => edge.source === reportId)
    .map((edge) => findNode(edge.target))
    .filter((node): node is KgFlowNode => node?.type === "dataStory")
    .map((node) =>
      usedInApiItem(
        node.id,
        node.data.title ?? node.id,
        node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
      ),
    );

  const sections: KgDataSourceUsedInResponse["sections"] = [];

  if (storyItems.length > 0) {
    sections.push({
      key: "stories",
      label: "Data stories",
      items: storyItems,
      pageInfo: emptyKgPageInfo(storyItems.length),
    });
  }

  return { sections };
};

export const mockFetchReportOverview = async (
  reportId: string,
): Promise<KgReportOverviewResponse> => {
  await delay();
  const node = findNode(reportId);
  if (!node || node.type !== "report") {
    throw new Error(`Report not found: ${reportId}`);
  }

  if (reportId === "rpt-trend") {
    return {
      ...RPT_TREND_OVERVIEW,
      name: node.data.title ?? RPT_TREND_OVERVIEW.name,
    };
  }

  if (reportId === "rpt-returns") {
    return {
      ...RPT_RETURNS_OVERVIEW,
      name: node.data.title ?? RPT_RETURNS_OVERVIEW.name,
    };
  }

  if (reportId === "rpt-gross") {
    return {
      ...RPT_GROSS_OVERVIEW,
      name: node.data.title ?? RPT_GROSS_OVERVIEW.name,
    };
  }

  return deriveReportOverviewFromGraph(reportId);
};

export const mockFetchReportTrace = async (
  reportId: string,
): Promise<KgReportTraceResponse> => {
  await delay();
  const node = findNode(reportId);
  if (!node || node.type !== "report") {
    throw new Error(`Report not found: ${reportId}`);
  }

  if (reportId === "rpt-trend") {
    return RPT_TREND_TRACE;
  }

  if (reportId === "rpt-returns") {
    return RPT_RETURNS_TRACE;
  }

  if (reportId === "rpt-gross") {
    return RPT_GROSS_TRACE;
  }

  return deriveReportTraceFromGraph(reportId);
};

export const mockFetchReportUsedIn = async (
  reportId: string,
): Promise<KgDataSourceUsedInResponse> => {
  await delay();
  const node = findNode(reportId);
  if (!node || node.type !== "report") {
    throw new Error(`Report not found: ${reportId}`);
  }

  if (reportId === "rpt-trend") {
    return RPT_TREND_USED_IN;
  }

  if (reportId === "rpt-returns") {
    return RPT_RETURNS_USED_IN;
  }

  if (reportId === "rpt-gross") {
    return RPT_GROSS_USED_IN;
  }

  return deriveReportUsedInFromGraph(reportId);
};

export const mockFetchReportActivity = async (
  reportId: string,
): Promise<KgReportActivityResponse> => {
  await delay();
  const node = findNode(reportId);
  if (!node || node.type !== "report") {
    throw new Error(`Report not found: ${reportId}`);
  }

  const sourceType =
    node.data.reportSourceType ?? inferReportSourceType(node.data.biName);

  if (sourceType !== "magicbi") {
    return { versions: [] };
  }

  if (reportId === "rpt-gross") {
    return RPT_GROSS_ACTIVITY;
  }

  return {
    versions: [
      {
        id: "v1",
        version: "V1",
        lastUpdated: "Live",
        updatedBy: "Magic BI system",
      },
    ],
  };
};

/** Figma data story overview — `dstory-q3` (node `2559:177714`). */
const DSTORY_Q3_OVERVIEW: KgDataStoryOverviewResponse = {
  id: "dstory-q3",
  name: "Q3 revenue review",
  externalUrl: MOCK_EXTERNAL_LINK,
  summary: "Quarterly revenue, AOV, and category mix performance.",
  owner: "Mia Park",
  sharedWith: ["Exec team", "Distribution list", "#exec-room"],
  artifacts: [
    {
      id: "art-q3-ppt",
      format: "PPT",
      label: "Q3 revenue deck",
      publishedOn: "Oct 14, 24",
    },
    {
      id: "art-q3-pdf",
      format: "PDF",
      label: "Q3 revenue PDF",
      publishedOn: "Oct 14, 24",
    },
    {
      id: "art-q3-memo",
      format: "DOC",
      label: "Q3 exec memo",
      publishedOn: "Oct 14, 24",
    },
    {
      id: "art-q3-slack",
      format: "Slack",
      label: "Q3 highlights slack",
      publishedOn: "Oct 14, 24",
    },
  ],
};

const DSTORY_Q3_TRACE: KgDataStoryTraceResponse = {
  sections: [
    {
      key: "dataSources",
      label: "Sources",
      items: [
        usedInApiItem("ds-snowflake", "Snowflake"),
        usedInApiItem("ds-tableau", "Tableau"),
      ],
      pageInfo: emptyKgPageInfo(2),
    },
    {
      key: "ontologies",
      label: "Ontologies",
      items: [
        usedInApiItem("ont-order", "Order"),
        usedInApiItem("ont-region", "Region"),
      ],
      pageInfo: emptyKgPageInfo(2),
    },
    {
      key: "tables",
      label: "Tables",
      items: [
        usedInApiItem("tbl-orders", "orders"),
        usedInApiItem("tbl-order-items", "order_items"),
        usedInApiItem("tbl-shipments", "shipments"),
        usedInApiItem("tbl-products", "products"),
        usedInApiItem("tbl-customers", "customers"),
        usedInApiItem("tbl-sales-report", "sales_report"),
      ],
      pageInfo: emptyKgPageInfo(6),
    },
  ],
};

const DSTORY_Q3_ACTIVITY: KgDataStoryActivityResponse = {
  versions: [
    {
      id: "v1",
      version: "V1",
      lastUpdated: "Oct 14, 2024",
      updatedBy: "Mia Park",
      context: "Story created",
    },
    {
      id: "v2",
      version: "V2",
      lastUpdated: "Oct 15, 2024",
      updatedBy: "Magic Bi Ai",
      context: "Q3 Exec summary",
    },
  ],
};

const getReportIdsForDataStory = (dataStoryId: string): string[] =>
  sampleEdges
    .filter((edge) => edge.target === dataStoryId)
    .map((edge) => findNode(edge.source))
    .filter((node): node is KgFlowNode => node?.type === "report")
    .map((node) => node.id);

const deriveDataStoryArtifactsFromGraph = (
  dataStoryId: string,
): KgDataStoryOverviewResponse["artifacts"] =>
  sampleEdges
    .filter((edge) => edge.source === dataStoryId)
    .map((edge) => findNode(edge.target))
    .filter((node): node is KgFlowNode => node?.type === "artifact")
    .map((node) => ({
      id: node.id,
      format: String(node.data.kind ?? "—"),
      label: node.data.title ?? node.id,
      publishedOn: "—",
    }));

const deriveDataStoryTraceFromGraph = (
  dataStoryId: string,
): KgDataStoryTraceResponse => {
  const reportIds = getReportIdsForDataStory(dataStoryId);
  const keys: KgTraceSectionKey[] = [
    "dataSources",
    "tables",
    "ontologies",
    "metrics",
  ];

  const nodeTypeByKey: Record<KgTraceSectionKey, KgFlowNode["type"]> = {
    dataSources: "dataSource",
    tables: "table",
    ontologies: "ontology",
    metrics: "metric",
  };

  const sections = keys
    .map((key) => {
      const seen = new Set<string>();
      const items: ReturnType<typeof usedInApiItem>[] = [];

      for (const reportId of reportIds) {
        for (const item of collectUpstreamByType(
          reportId,
          nodeTypeByKey[key],
        )) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            items.push(item);
          }
        }
      }

      return {
        key,
        label: TRACE_SECTION_LABELS[key],
        items,
        pageInfo: emptyKgPageInfo(items.length),
      };
    })
    .filter((section) => section.items.length > 0);

  return { sections };
};

const deriveDataStoryOverviewFromGraph = (
  dataStoryId: string,
): KgDataStoryOverviewResponse => {
  const node = findNode(dataStoryId);
  if (!node || node.type !== "dataStory") {
    throw new Error(`Data story not found: ${dataStoryId}`);
  }

  return {
    id: dataStoryId,
    name: node.data.title ?? dataStoryId,
    externalUrl: node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
    summary: "",
    owner: "—",
    sharedWith: [],
    artifacts: deriveDataStoryArtifactsFromGraph(dataStoryId),
  };
};

export const mockFetchDataStoryOverview = async (
  dataStoryId: string,
): Promise<KgDataStoryOverviewResponse> => {
  await delay();
  const node = findNode(dataStoryId);
  if (!node || node.type !== "dataStory") {
    throw new Error(`Data story not found: ${dataStoryId}`);
  }

  if (dataStoryId === "dstory-q3") {
    return {
      ...DSTORY_Q3_OVERVIEW,
      name: node.data.title ?? DSTORY_Q3_OVERVIEW.name,
    };
  }

  return deriveDataStoryOverviewFromGraph(dataStoryId);
};

export const mockFetchDataStoryTrace = async (
  dataStoryId: string,
): Promise<KgDataStoryTraceResponse> => {
  await delay();
  const node = findNode(dataStoryId);
  if (!node || node.type !== "dataStory") {
    throw new Error(`Data story not found: ${dataStoryId}`);
  }

  if (dataStoryId === "dstory-q3") {
    return DSTORY_Q3_TRACE;
  }

  return deriveDataStoryTraceFromGraph(dataStoryId);
};

export const mockFetchDataStoryActivity = async (
  dataStoryId: string,
): Promise<KgDataStoryActivityResponse> => {
  await delay();
  const node = findNode(dataStoryId);
  if (!node || node.type !== "dataStory") {
    throw new Error(`Data story not found: ${dataStoryId}`);
  }

  if (dataStoryId === "dstory-q3") {
    return DSTORY_Q3_ACTIVITY;
  }

  return { versions: [] };
};

/** Figma artifact overview — `art-q3-ppt` (node `2559:178393`). */
const ART_Q3_PPT_OVERVIEW: KgArtifactOverviewResponse = {
  id: "art-q3-ppt",
  name: "Q3 revenue deck",
  externalUrl: MOCK_EXTERNAL_LINK,
  format: "PPT",
  sharedWith: "Exec team",
  updatedOn: "Live",
};

const ART_Q3_PPT_ACTIVITY: KgArtifactActivityResponse = {
  summary: {
    title: "Q3 revenue deck",
    date: "Oct 12",
    layoutTemplate: "Layout template",
  },
  events: [
    {
      id: "evt-1",
      type: "Published",
      timestamp: "Oct 14, 2024",
      published: "Mia Park",
    },
    {
      id: "evt-2",
      type: "Accessed",
      timestamp: "Yesterday",
      published: "Exec team",
    },
  ],
};

const getParentDataStoryId = (artifactId: string): string | undefined => {
  const edge = sampleEdges.find(
    (entry) =>
      entry.target === artifactId &&
      findNode(entry.source)?.type === "dataStory",
  );
  return edge?.source;
};

const deriveArtifactOverviewFromGraph = (
  artifactId: string,
): KgArtifactOverviewResponse => {
  const node = findNode(artifactId);
  if (!node || node.type !== "artifact") {
    throw new Error(`Artifact not found: ${artifactId}`);
  }

  const parentDataStoryId = getParentDataStoryId(artifactId);
  let sharedWith = "—";
  if (parentDataStoryId === "dstory-q3") {
    sharedWith = DSTORY_Q3_OVERVIEW.sharedWith[0] ?? "—";
  } else if (parentDataStoryId) {
    const parentOverview = deriveDataStoryOverviewFromGraph(parentDataStoryId);
    sharedWith = parentOverview.sharedWith[0] ?? "—";
  }

  return {
    id: artifactId,
    name: node.data.title ?? artifactId,
    externalUrl: node.data.details?.externalUrl ?? MOCK_EXTERNAL_LINK,
    format: String(node.data.kind ?? "—"),
    sharedWith,
    updatedOn: "Live",
  };
};

export const mockFetchArtifactOverview = async (
  artifactId: string,
): Promise<KgArtifactOverviewResponse> => {
  await delay();
  const node = findNode(artifactId);
  if (!node || node.type !== "artifact") {
    throw new Error(`Artifact not found: ${artifactId}`);
  }

  if (artifactId === "art-q3-ppt") {
    return {
      ...ART_Q3_PPT_OVERVIEW,
      name: node.data.title ?? ART_Q3_PPT_OVERVIEW.name,
    };
  }

  return deriveArtifactOverviewFromGraph(artifactId);
};

const deriveArtifactActivityFromGraph = (
  artifactId: string,
): KgArtifactActivityResponse => {
  const overview = deriveArtifactOverviewFromGraph(artifactId);
  const parentDataStoryId = getParentDataStoryId(artifactId);

  const events =
    parentDataStoryId === "dstory-q3"
      ? ART_Q3_PPT_ACTIVITY.events.map((event, index) => ({
          ...event,
          id: `${artifactId}-${event.id}-${index}`,
        }))
      : [];

  return {
    summary: {
      title: overview.name,
      date: overview.updatedOn === "Live" ? "Oct 12" : overview.updatedOn,
      layoutTemplate: "Layout template",
    },
    events,
  };
};

export const mockFetchArtifactActivity = async (
  artifactId: string,
): Promise<KgArtifactActivityResponse> => {
  await delay();
  const node = findNode(artifactId);
  if (!node || node.type !== "artifact") {
    throw new Error(`Artifact not found: ${artifactId}`);
  }

  if (artifactId === "art-q3-ppt") {
    return ART_Q3_PPT_ACTIVITY;
  }

  return deriveArtifactActivityFromGraph(artifactId);
};
